import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { usersApi } from '../lib/api.js';
import type { UserEvent, UserSession } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import { formatDuration } from '../lib/formatters.js';
import { useDateRange } from '../hooks/useDateRange.js';
import { DateRangeSelector } from '../components/DateRangeSelector.js';

/**
 * @graph-node {Page}
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business ユーザー行動分析ページ。ユーザーID検索でセッション別イベントタイムライン・プロパティ・ステータスを表示
 * @graph-connects usersApi [calls] Users APIクライアント呼び出し
 * @graph-connects queryKeys [calls] クエリキー生成
 * @graph-connects useDateRange [calls] 日付範囲管理フック
 * @graph-connects formatDuration [calls] 時間フォーマット
 * @graph-connects DateRangeSelector [calls] 日付範囲セレクター描画
 * @graph-connects UserSummary [calls] ユーザーサマリー描画
 * @graph-connects SessionCard [calls] セッションカード描画
 * @graph-connects EventRow [calls] イベント行描画
 * @graph-connects EventTypeBadge [calls] イベントタイプバッジ描画
 * @graph-connects PropertiesDisplay [calls] プロパティ展開表示描画
 * @graph-connects extractStatuses [calls] ステータスプロパティ抽出
 * @graph-connects formatTime [calls] 時刻フォーマット
 * @graph-connects formatDateTime [calls] 日時フォーマット
 * @graph-connects formatPropValue [calls] プロパティ値フォーマット
 * @graph-connects parseProperties [calls] プロパティJSON解析
 */
export const Route = createFileRoute('/users')({
  component: UsersPage,
});

/** @graph-connects none */
const EVENT_TYPE_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  screen_state: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: '状態' },
  user_action: { bg: 'bg-green-500/20', text: 'text-green-400', label: '操作' },
  conversion: { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'CV' },
  error: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'エラー' },
};

// タイムラインに表示しないイベント（滞在時間は screen_rendered に紐付け済み）
/** @graph-connects none */
const HIDDEN_EVENTS = new Set(['page_exit', 'scroll_depth']);

// プロパティ表示から除外するキー（タイムラインで自明 or 重複）
/** @graph-connects none */
const HIDDEN_PROPS = new Set(['previous_screen', 'is_applying_later_payment']);

// ステータスとして抽出するプロパティキー
/** @graph-connects none */
const STATUS_KEYS = [
  'membership_status',
  'plan_id',
  'rental_status',
  'payment_type',
  'option_ids',
  'is_come_back',
  'is_waiting',
  'prefecture',
];

// 都道府県コード → 名称
/** @graph-connects none */
const PREFECTURE_NAMES: Record<number, string> = {
  1: '北海道',
  2: '青森県',
  3: '岩手県',
  4: '宮城県',
  5: '秋田県',
  6: '山形県',
  7: '福島県',
  8: '茨城県',
  9: '栃木県',
  10: '群馬県',
  11: '埼玉県',
  12: '千葉県',
  13: '東京都',
  14: '神奈川県',
  15: '新潟県',
  16: '富山県',
  17: '石川県',
  18: '福井県',
  19: '山梨県',
  20: '長野県',
  21: '岐阜県',
  22: '静岡県',
  23: '愛知県',
  24: '三重県',
  25: '滋賀県',
  26: '京都府',
  27: '大阪府',
  28: '兵庫県',
  29: '奈良県',
  30: '和歌山県',
  31: '鳥取県',
  32: '島根県',
  33: '岡山県',
  34: '広島県',
  35: '山口県',
  36: '徳島県',
  37: '香川県',
  38: '愛媛県',
  39: '高知県',
  40: '福岡県',
  41: '佐賀県',
  42: '長崎県',
  43: '熊本県',
  44: '大分県',
  45: '宮崎県',
  46: '鹿児島県',
  47: '沖縄県',
};

// ============================================================
// ヘルパー
// ============================================================

/** @graph-connects none */
function formatTime(isoStr: string | null | undefined): string {
  if (!isoStr) return '-';
  const d = new Date(isoStr);
  return d.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

/** @graph-connects none */
function formatDateTime(isoStr: string | null | undefined): string {
  if (!isoStr) return '-';
  const d = new Date(isoStr);
  return d.toLocaleString('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** @graph-connects none */
function formatPropValue(key: string, val: unknown): string {
  if (val === null || val === undefined) return 'null';
  if (key === 'prefecture') {
    const code = typeof val === 'string' ? Number(val) : val;
    if (typeof code === 'number' && PREFECTURE_NAMES[code]) {
      return PREFECTURE_NAMES[code];
    }
  }
  if (typeof val === 'object') return JSON.stringify(val);
  if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
    return String(val);
  }
  return JSON.stringify(val);
}

/** @graph-connects none */
function parseProperties(
  props: Record<string, unknown> | string | null,
): Record<string, unknown> | null {
  if (!props) return null;
  if (typeof props === 'string') {
    try {
      return JSON.parse(props) as Record<string, unknown>;
    } catch (error) {
      // SyntaxError は壊れた properties として null で継続。他は再 throw
      if (!(error instanceof SyntaxError)) {
        throw error;
      }
      return null;
    }
  }
  return props;
}

/** @graph-connects none */
function extractStatuses(events: UserEvent[]): Record<string, unknown> {
  const status: Record<string, unknown> = {};
  for (const ev of events) {
    if (ev.event !== 'screen_rendered') continue;
    const props = parseProperties(ev.properties);
    if (!props) continue;
    for (const key of STATUS_KEYS) {
      if (key in props && props[key] != null) {
        status[key] = props[key];
      }
    }
  }
  return status;
}

// ============================================================
// イベントタイプバッジ
// ============================================================

/** @graph-connects none */
function EventTypeBadge({ eventType }: { eventType: string }) {
  const style = EVENT_TYPE_STYLES[eventType] ?? EVENT_TYPE_STYLES.user_action;
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono font-medium ${style.bg} ${style.text}`}
    >
      {style.label}
    </span>
  );
}

// ============================================================
// プロパティ表示（展開可能）
// ============================================================

/** @graph-connects none */
function PropertiesDisplay({ properties: raw }: { properties: Record<string, unknown> | null }) {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const properties = parseProperties(raw);

  const filtered = properties
    ? Object.fromEntries(Object.entries(properties).filter(([k]) => !HIDDEN_PROPS.has(k)))
    : null;

  if (!filtered || Object.keys(filtered).length === 0) return null;

  return (
    <div className="mt-1">
      <button
        onClick={() => {
          setIsOpen(!isOpen);
        }}
        className="text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] transition-colors"
      >
        {isOpen ? '▼' : '▶'} {t('users.properties')} ({Object.keys(filtered).length})
      </button>
      {isOpen && (
        <div className="mt-1 p-2 rounded bg-[var(--color-bg-hover)] text-[10px] overflow-x-auto max-h-48 space-y-0.5">
          {Object.entries(filtered).map(([key, val]) => {
            const label = t(`users.propNames.${key}`, { defaultValue: '' }) || key;
            return (
              <div key={key} className="flex gap-2">
                <span className="text-[var(--color-text-muted)] shrink-0">{label}:</span>
                <span className="text-[var(--color-text-secondary)] font-mono break-all">
                  {formatPropValue(key, val)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================
// イベント行
// ============================================================

/** @graph-connects none */
function EventRow({ event }: { event: UserEvent }) {
  const { t } = useTranslation();
  const isError = event.eventType === 'error';
  const isScreenRendered = event.event === 'screen_rendered';
  const eventLabel = t(`users.eventNames.${event.event}`, { defaultValue: '' }) || event.event;
  const errorProps = isError ? parseProperties(event.properties) : null;

  return (
    <div className={`flex items-start gap-3 py-2 px-3 rounded-md ${isError ? 'bg-red-500/5' : ''}`}>
      {/* タイムスタンプ */}
      <span className="text-[11px] text-[var(--color-text-muted)] font-mono whitespace-nowrap pt-0.5">
        {formatTime(event.timestamp)}
      </span>

      {/* イベントタイプバッジ */}
      <div className="pt-0.5">
        <EventTypeBadge eventType={event.eventType} />
      </div>

      {/* イベント情報 */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-[var(--color-text-primary)]">
            {event.screen}
          </span>
          <span className="text-[11px] text-[var(--color-text-secondary)]">{eventLabel}</span>
          {isScreenRendered && event.dwellTimeMs != null && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--color-bg-hover)] text-[var(--color-text-muted)] font-mono">
              {formatDuration(event.dwellTimeMs)}
            </span>
          )}
        </div>

        {/* エラー時のプロパティハイライト */}
        {isError && errorProps && (
          <div className="mt-1 text-[11px] text-[var(--color-error)]">
            {errorProps.error_message != null
              ? typeof errorProps.error_message === 'string'
                ? errorProps.error_message
                : JSON.stringify(errorProps.error_message)
              : JSON.stringify(errorProps)}
          </div>
        )}

        {/* プロパティ展開 */}
        {!isError && <PropertiesDisplay properties={event.properties} />}
      </div>
    </div>
  );
}

// ============================================================
// セッションカード
// ============================================================

/** @graph-connects none */
function SessionCard({ session }: { session: UserSession }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);
  const status = useMemo(() => extractStatuses(session.events), [session.events]);
  const hasStatus = Object.keys(status).length > 0;

  return (
    <div className="card animate-fade-in-up">
      {/* セッションヘッダー */}
      <button
        onClick={() => {
          setIsOpen(!isOpen);
        }}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-3">
          <span className="text-xs font-mono text-[var(--color-text-muted)]">
            {isOpen ? '▼' : '▶'}
          </span>
          <div>
            <div className="text-sm font-medium text-[var(--color-text-primary)]">
              {formatDateTime(session.startTime)} — {formatTime(session.endTime)}
            </div>
            <div className="text-[11px] text-[var(--color-text-muted)] flex gap-3">
              <span>
                {session.deviceType} / {session.osName} / {session.browserName}
              </span>
              <span>
                {session.eventCount} {t('users.events')}
              </span>
            </div>
          </div>
        </div>
        <span className="text-[10px] font-mono text-[var(--color-text-muted)] truncate max-w-[200px]">
          {session.sessionId}
        </span>
      </button>

      {/* セッション内容 */}
      {isOpen && (
        <div className="mt-3 flex gap-4">
          {/* タイムライン */}
          <div className="flex-1 border-l-2 border-[var(--color-border)] ml-2 space-y-0.5">
            {session.events
              .filter((ev) => !HIDDEN_EVENTS.has(ev.event))
              .map((ev) => (
                <EventRow key={ev.eventId} event={ev} />
              ))}
          </div>

          {/* ステータスサイドバー */}
          {hasStatus && (
            <div className="w-48 shrink-0 hidden lg:block">
              <div className="sticky top-4 p-3 rounded-lg bg-[var(--color-bg-hover)]">
                <h4 className="text-[10px] font-semibold text-[var(--color-text-muted)] uppercase mb-2">
                  {t('users.userStatus')}
                </h4>
                <div className="space-y-1.5">
                  {Object.entries(status).map(([key, val]) => (
                    <div key={key}>
                      <div className="text-[10px] text-[var(--color-text-muted)]">
                        {t(`users.propNames.${key}`, { defaultValue: key })}
                      </div>
                      <div className="text-[11px] text-[var(--color-text-primary)] font-mono">
                        {formatPropValue(key, val)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// ユーザーサマリー
// ============================================================

/** @graph-connects none */
function UserSummary({
  user,
  totalSessions,
  totalEvents,
  sessions,
}: {
  user: { userId: string; internalId: number; visitorId: string | null };
  totalSessions: number;
  totalEvents: number;
  sessions: UserSession[];
}) {
  const { t } = useTranslation();
  const lastSession = sessions[sessions.length - 1];

  return (
    <div className="card animate-fade-in-up">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <div>
          <div className="text-[10px] text-[var(--color-text-muted)] uppercase">
            {t('users.userId')}
          </div>
          <div className="text-sm font-mono text-[var(--color-text-primary)]">{user.userId}</div>
        </div>
        <div>
          <div className="text-[10px] text-[var(--color-text-muted)] uppercase">
            {t('users.internalId')}
          </div>
          <div className="text-sm font-mono text-[var(--color-text-primary)]">
            {user.internalId}
          </div>
        </div>
        {user.visitorId && (
          <div>
            <div className="text-[10px] text-[var(--color-text-muted)] uppercase">
              {t('users.visitorId')}
            </div>
            <div className="text-sm font-mono text-[var(--color-text-primary)] truncate">
              {user.visitorId}
            </div>
          </div>
        )}
        <div>
          <div className="text-[10px] text-[var(--color-text-muted)] uppercase">
            {t('users.sessions')}
          </div>
          <div className="text-sm font-semibold text-[var(--color-text-primary)]">
            {totalSessions}
          </div>
        </div>
        <div>
          <div className="text-[10px] text-[var(--color-text-muted)] uppercase">
            {t('users.events')}
          </div>
          <div className="text-sm font-semibold text-[var(--color-text-primary)]">
            {totalEvents}
          </div>
        </div>
        {sessions.length > 0 && (
          <div>
            <div className="text-[10px] text-[var(--color-text-muted)] uppercase">
              {t('users.lastActive')}
            </div>
            <div className="text-sm text-[var(--color-text-primary)]">
              {formatDateTime(lastSession.endTime)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// メインページ
// ============================================================

/** @graph-connects ux-insights-api [calls] UX Insights APIを呼び出す */
function UsersPage() {
  const { t } = useTranslation();
  const { preset, setPreset, startDate, endDate, setCustomDates } = useDateRange('7d');
  const [searchInput, setSearchInput] = useState('');
  const [searchUserId, setSearchUserId] = useState<string | null>(null);

  const handleSearch = () => {
    const trimmed = searchInput.trim();
    if (trimmed) {
      setSearchUserId(trimmed);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const timelineQuery = useQuery({
    queryKey: queryKeys.users.timeline(searchUserId ?? '', startDate, endDate),
    queryFn: () => usersApi.getTimeline(searchUserId ?? '', startDate, endDate),
    enabled: searchUserId !== null,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  return (
    <div className="space-y-6">
      {/* ページヘッダー */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            {t('users.title')}
          </h1>
          <p className="text-sm text-[var(--color-text-muted)]">{t('users.description')}</p>
        </div>
        <DateRangeSelector
          preset={preset}
          startDate={startDate}
          endDate={endDate}
          onPresetChange={setPreset}
          onCustomDatesChange={setCustomDates}
        />
      </div>

      {/* 検索バー */}
      <div className="card animate-fade-in-up">
        <div className="flex gap-3">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            placeholder={t('users.searchPlaceholder')}
            className="flex-1 px-4 py-2.5 rounded-lg bg-[var(--color-bg-hover)] text-[var(--color-text-primary)] text-sm border border-[var(--color-border)] focus:border-[var(--color-accent-primary)] focus:outline-none placeholder:text-[var(--color-text-muted)] font-mono"
          />
          <button
            onClick={handleSearch}
            disabled={!searchInput.trim()}
            className="px-6 py-2.5 rounded-lg bg-[var(--color-accent-primary)] text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40"
          >
            {t('users.search')}
          </button>
        </div>
      </div>

      {/* 結果 */}
      {!searchUserId && (
        <div className="text-center py-16 text-[var(--color-text-muted)]">
          <p className="text-lg">👤</p>
          <p className="text-sm mt-2">{t('users.noSearch')}</p>
        </div>
      )}

      {timelineQuery.isLoading && (
        <div className="space-y-4">
          <div className="skeleton-shimmer h-24 w-full rounded-lg" />
          <div className="skeleton-shimmer h-48 w-full rounded-lg" />
          <div className="skeleton-shimmer h-48 w-full rounded-lg" />
        </div>
      )}

      {timelineQuery.isError && (
        <div className="card animate-fade-in-up text-center py-8">
          <p className="text-[var(--color-error)] text-sm">
            {timelineQuery.error instanceof Error
              ? timelineQuery.error.message
              : t('users.noResults')}
          </p>
        </div>
      )}

      {timelineQuery.data && (
        <>
          {/* ユーザーサマリー */}
          <UserSummary
            user={timelineQuery.data.user}
            totalSessions={timelineQuery.data.totalSessions}
            totalEvents={timelineQuery.data.totalEvents}
            sessions={timelineQuery.data.sessions}
          />

          {/* セッションタイムライン */}
          {timelineQuery.data.sessions.length === 0 ? (
            <div className="card animate-fade-in-up text-center py-8">
              <p className="text-sm text-[var(--color-text-muted)]">{t('common.noData')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {timelineQuery.data.sessions.map((session) => (
                <SessionCard key={session.sessionId} session={session} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
